/** Itineraries catalog grid. Cross-tenant list of Itinerary_Group packages with
 *  tenant scope + search + a published filter, plus batch publish/unpublish,
 *  edit-display-settings, and delete over the selection (gated
 *  `console_itineraries_manage`; unavailable, with the reason, while the selection
 *  holds an operator's placeholder for new tours; the backend leaves out in-use ones,
 *  and the grid says so). Row → detail; "New" opens the create dialog.
 *
 *  In the app: the record header's status chip (Live / Coming soon / Not published /
 *  Placeholder, never "Draft"), from the row's own facts. Hidden until asked for, the
 *  record work's Placeholder, Catalog itinerary, Source and App button columns, in the
 *  shared catalog words; none of them sorts (the backend orders by none of them, and
 *  `source_name` is read for the page's rows only, Appendix A 3g). */
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CropFreeIcon from '@mui/icons-material/CropFree';
import ImageIcon from '@mui/icons-material/Image';
import PublicIcon from '@mui/icons-material/Public';
import PublicOffIcon from '@mui/icons-material/PublicOff';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import { Box, Button, CircularProgress, TextField } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as itinerariesApi from '@/api/itineraries';
import type { ItineraryListItem } from '@/api/itineraries';
import { deleteBatchAction, type BatchActionDef } from '@/components/DataGrid/batchTypes';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import { TenantLinkCell } from '@/components/TenantMark/TenantMark';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { AiImportEntryButton } from '@/features/ai-import/AiImportEntryButton';
import { batchEditAction } from '@/features/batch-edit/batchEditAction';
import { CatalogSourceCell, IconWords } from '@/features/catalog/catalogCells';
import { CatalogStatusChip } from '@/features/catalog/CatalogStatusChip';
import {
  CATALOG_STATUS_LABEL,
  catalogSourceName,
  ctaBehaviourText,
  itineraryRowStatus,
  NO_CATALOG_ITINERARY,
  PLACEHOLDER_LABEL,
  placeholderDeleteReason,
} from '@/features/catalog/words';
import { entityExportBatchAction } from '@/features/exports/EntityExportDialog';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { formatRelativeTime } from '@/utils/format';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds, singleTenantId, tenantIdsToApi } from '@/scope/tenantIds';
import { useTenantScopeList } from '@/scope/tenantScope';

import { itineraryBatchDeleteRefusals } from './deleteRefusal';
import { EditDisplaySettingsDialog } from './EditDisplaySettingsDialog';
import { ItineraryCreateDialog } from './ItineraryCreateDialog';

const publishedToApi = (v: string) => (v === 'true' || v === 'false' ? v : undefined);

/** A placeholder for new tours is never deletable (the backend refuses it too). */
function placeholderUnavailable(rows: readonly unknown[]): string | null {
  const ids = (rows as readonly ItineraryListItem[]).filter((r) => r.placeholder === true).map((r) => r.id);
  return ids.length > 0 ? placeholderDeleteReason(ids) : null;
}

const FILTERS: FilterDef[] = [
  { param: 'tenant', apiParam: 'tour_operator_id', toApi: tenantIdsToApi, tenantScope: 'multi' },
  { param: 'published', apiParam: 'published', toApi: publishedToApi },
];
const PRESET_FILTERS = makeResourceFiltersAdapter(FILTERS);
const DEFAULT_ORDER = [
  'id',
  'name',
  'tenant',
  'published',
  'placeholder',
  'default_missing',
  'has_cover_image',
  'show_in_catalog',
  'custom_id',
  'source_name',
  'day_count',
  'cost',
  'currency',
  'cost_prefix',
  'cost_unit',
  'booking_url',
  'cta_button_behavior',
  'duration_display',
  'sequence',
  'updated_ts',
];

/** Columns present but hidden until toggled on via the column menu. */
const DEFAULT_HIDDEN = [
  'placeholder',
  'default_missing',
  'show_in_catalog',
  'custom_id',
  'source_name',
  'cost_prefix',
  'cost_unit',
  'booking_url',
  'cta_button_behavior',
  'duration_display',
];

export function ItinerariesListPage() {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_itineraries_manage');
  const [createOpen, setCreateOpen] = useState(false);
  useCreateIntent(() => setCreateOpen(true));

  const presets = useGridPresets({
    grid: 'itineraries',
    defaultOrder: DEFAULT_ORDER,
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: 'sequence',
    filters: PRESET_FILTERS,
  });

  const {
    extra,
    getParam,
    setParam,
    resetAll: resetAllFilters,
    anyActive: anyFilterActive,
  } = useGridFilters(FILTERS, presets.filtersLayer);

  // The operators the effective tenant filter names (URL > preset > scope): the
  // picker's value, and what a create dialog pre-selects / features (D9).
  const pageTenants = pageTenantsFrom(getParam('tenant'), useTenantScopeList());
  // The single-tenant extras (batch tenant, AI import) act only when the filter
  // names exactly one operator (D5).
  const tenantId = singleTenantId(pageTenants.map((t) => t.id));
  const publishedParam = getParam('published') ?? '';

  const columns = useMemo<GridColDef<ItineraryListItem>[]>(
    () => [
      { field: 'id', headerName: 'ID', width: 80 },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 220,
        renderCell: (p) => (
          <RouterLink to={`/itineraries/${p.row.id}`} style={{ color: '#0a1a3f', fontWeight: 500 }}>
            {p.row.name || `#${p.row.id}`}
          </RouterLink>
        ),
      },
      {
        field: 'tenant',
        headerName: 'Operator',
        width: 170,
        sortable: false,
        valueGetter: (_, row) => row.tenant?.name ?? '',
        renderCell: (p) => <TenantLinkCell tenant={p.row.tenant} />,
      },
      {
        field: 'published',
        headerName: 'In the app',
        width: 140,
        sortable: false,
        description:
          'As on the record: Live, Coming soon (published, no catalog itinerary yet), Not published, or Placeholder for new tours (never shown).',
        valueGetter: (_, row) => CATALOG_STATUS_LABEL[itineraryRowStatus(row)],
        renderCell: (p) => <CatalogStatusChip status={itineraryRowStatus(p.row)} />,
      },
      // The record work's columns (plan §10.4), hidden by default and never sorted.
      {
        field: 'placeholder',
        headerName: 'Placeholder',
        width: 210,
        sortable: false,
        valueGetter: (_, row) => (row.placeholder ? PLACEHOLDER_LABEL : ''),
        renderCell: (p) =>
          p.row.placeholder ? <IconWords icon={CropFreeIcon}>{PLACEHOLDER_LABEL}</IconWords> : null,
      },
      {
        field: 'default_missing',
        headerName: 'Catalog itinerary',
        width: 200,
        sortable: false,
        description:
          'Without its catalog itinerary (day plan), a published itinerary shows “Coming soon” in the app.',
        valueGetter: (_, row) => (row.default_missing ? NO_CATALOG_ITINERARY : ''),
        renderCell: (p) =>
          p.row.default_missing ? (
            <IconWords icon={ScheduleOutlinedIcon}>{NO_CATALOG_ITINERARY}</IconWords>
          ) : null,
      },
      {
        field: 'has_cover_image',
        headerName: 'Cover',
        width: 80,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        description: 'Whether the package has a cover image.',
        renderCell: (p) =>
          p.row.has_cover_image ? (
            <ImageIcon fontSize="small" color="action" titleAccess="Has cover image" />
          ) : null,
      },
      {
        field: 'show_in_catalog',
        headerName: 'In catalog',
        width: 100,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (p) =>
          p.row.show_in_catalog ? (
            <CheckIcon fontSize="small" color="success" titleAccess="Shown in catalog" />
          ) : null,
      },
      {
        field: 'custom_id',
        headerName: 'Custom ID',
        width: 140,
        sortable: false,
        valueGetter: (_, row) => row.custom_id ?? '',
        renderCell: (p) => p.row.custom_id || '—',
      },
      {
        field: 'source_name',
        headerName: 'Source',
        width: 170,
        sortable: false,
        description: 'The import source of the newest import that touched it.',
        valueGetter: (_, row) => catalogSourceName(row.source_name),
        renderCell: (p) => <CatalogSourceCell name={p.row.source_name} />,
      },
      {
        field: 'day_count',
        headerName: 'Days',
        width: 90,
        sortable: false,
        renderCell: (p) => p.row.day_count ?? '—',
      },
      {
        field: 'cost',
        headerName: 'Cost',
        width: 110,
        sortable: false,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_, row) => row.cost ?? '',
        renderCell: (p) => p.row.cost || '—',
      },
      {
        field: 'currency',
        headerName: 'Currency',
        width: 100,
        sortable: false,
        valueGetter: (_, row) => row.currency?.code ?? '',
        renderCell: (p) => p.row.currency?.code || '—',
      },
      {
        field: 'cost_prefix',
        headerName: 'Cost prefix',
        width: 120,
        sortable: false,
        valueGetter: (_, row) => row.cost_prefix ?? '',
        renderCell: (p) => p.row.cost_prefix || '—',
      },
      {
        field: 'cost_unit',
        headerName: 'Cost unit',
        width: 120,
        sortable: false,
        valueGetter: (_, row) => row.cost_unit ?? '',
        renderCell: (p) => p.row.cost_unit || '—',
      },
      {
        field: 'booking_url',
        headerName: 'Booking URL',
        flex: 1,
        minWidth: 200,
        sortable: false,
        valueGetter: (_, row) => row.booking_url ?? '',
        renderCell: (p) => p.row.booking_url || '—',
      },
      {
        field: 'cta_button_behavior',
        headerName: 'App button',
        width: 300,
        sortable: false,
        description:
          'What the app button does. The app shows it only when it has text; the record says whether it does.',
        valueGetter: (_, row) => ctaBehaviourText(row.cta_button_behavior),
        renderCell: (p) => {
          const text = ctaBehaviourText(p.row.cta_button_behavior);
          return <span title={text}>{text}</span>;
        },
      },
      {
        field: 'duration_display',
        headerName: 'Duration',
        width: 140,
        sortable: false,
        valueGetter: (_, row) => row.duration_display ?? '',
        renderCell: (p) => p.row.duration_display || '—',
      },
      { field: 'sequence', headerName: 'Seq', width: 90, sortable: true },
      {
        field: 'updated_ts',
        headerName: 'Updated',
        width: 150,
        sortable: true,
        valueGetter: (_, row) => row.updated_ts ?? '',
        renderCell: (p) => (p.row.updated_ts ? formatRelativeTime(p.row.updated_ts) : '—'),
      },
    ],
    [],
  );

  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      itinerariesApi.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? 'sequence',
        search: params.search,
        ...(params.extra as Record<string, never>),
      }),
    [],
  );

  // Publish / unpublish + edit-display-settings go through the generic async
  // `/itineraries/batch/` dispatch (background task). Unlike the legacy sync
  // endpoints these support "select all matching" — the server resolves the set.
  const batchActions = useMemo<BatchActionDef[]>(
    () => [
      {
        key: 'publish',
        label: 'Publish',
        icon: <PublicIcon fontSize="small" />,
        permission: 'console_itineraries_manage',
        action: 'publish',
        confirmText: (n) =>
          `Publish ${n.toLocaleString()} itinerary(s)? This runs in the background.`,
      },
      {
        key: 'unpublish',
        label: 'Unpublish',
        icon: <PublicOffIcon fontSize="small" />,
        permission: 'console_itineraries_manage',
        action: 'unpublish',
        confirmText: (n) =>
          `Unpublish ${n.toLocaleString()} itinerary(s)? This runs in the background.`,
      },
      {
        key: 'set_display_settings',
        label: 'Edit display settings',
        icon: <TuneIcon fontSize="small" />,
        permission: 'console_itineraries_manage',
        action: 'set_display_settings',
        dialog: (ctx) => <EditDisplaySettingsDialog ctx={ctx} />,
      },
      entityExportBatchAction({
        permission: 'console_itineraries_export',
        entity: 'itinerary',
        resource: 'itineraries',
        noun: 'itinerary(s)',
      }),
      batchEditAction('itineraries', 'itinerary'),
      {
        ...deleteBatchAction('console_itineraries_manage'),
        unavailableReason: placeholderUnavailable,
        refusals: itineraryBatchDeleteRefusals,
      },
    ],
    [],
  );

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'tenant',
      priority: 5,
      active: pageTenants.length > 0,
      node: (
        <Box sx={{ minWidth: 280 }}>
          <TenantPicker
            multiple
            value={pageTenants}
            onChange={(next) => setParam('tenant', formatTenantIds(next.map((t) => t.id)))}
          />
        </Box>
      ),
    },
    {
      key: 'published',
      priority: 4,
      active: publishedParam !== '',
      node: (
        // A native select always shows its option ("All" included), so the label
        // must sit above it: left to MUI's empty-value rule it lay over the value
        // and read "Allblished".
        <TextField
          select
          label="Published"
          size="small"
          sx={{ minWidth: 160 }}
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
          value={publishedParam}
          onChange={(e) => setParam('published', e.target.value || null)}
        >
          <option value="">All</option>
          <option value="true">Published</option>
          <option value="false">{CATALOG_STATUS_LABEL.not_published}</option>
        </TextField>
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="itineraries"
        title="Itineraries"
        subtitle="Cross-tenant itinerary-catalog packages."
        actions={
          <>
            <AiImportEntryButton type="itinerary" tenantId={tenantId} />
            {canManage && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
              >
                New itinerary
              </Button>
            )}
          </>
        }
      />

      <ItineraryCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        pageTenants={pageTenants}
      />

      <ResponsiveFilterBar items={toolbarItems} />

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <ResourceDataGrid<ItineraryListItem>
          queryKey={['itineraries']}
          queryFn={queryFn}
          columns={orderColumns(columns, presets.orderedFields)}
          extra={extra}
          batchResource="itineraries"
          // Declared unconditionally (batch-gating lesson, 2026-07-24): each
          // action carries its own `permission`; the menu filters, the
          // backend re-checks. A page-level gate would hide the checkbox
          // column from export-only operators.
          batchActions={batchActions}
          batchTenantFilterId={tenantId}
          defaultSort={{ field: 'sequence', sort: 'asc' }}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={presets.onColumnOrderChange}
          headerToolbar={{
            onResetAllFilters: resetAllFilters,
            resetDisabled: !anyFilterActive,
            search: true,
            searchPlaceholder: 'Name or custom ID',
            exportFileName: 'itineraries',
            presets: presets.toolbar,
          }}
        />
      )}
    </Box>
  );
}
